<script setup lang="ts">
    import { ModelType } from '~/app/base/schemas/BaseSchema';
    import EndpointFactory from '~/app/factories/EndpointFactory';
    import InvoiceEndpoint from '~/app/invoicing/endpoints/InvoiceEndpoint';
    import Invoice from '~/app/invoicing/models/Invoice';
    import { useAiAssistant } from '~/composables/useAiAssistant';
    import { useEntityPreview } from '~/composables/useEntityPreview';
    import useValidFileFormat from '~/composables/useValidFileFormat';
    import { ENTITY_LABEL_KEY_MAP } from '~/utils/aiEntityMeta';
    import { replaceInvoiceVariables } from '~/utils/aiInvoiceVariables';
    import { useAuthStore } from '~/stores/authStore';

    const props = defineProps<{
        type: string;
        id: string;
    }>();

    defineEmits<{
        close: [];
    }>();

    const typeRef = computed(() => props.type);
    const idRef = computed(() => props.id);
    const { loading, error, fetchEntity, setRefreshCallback } = useEntityPreview(typeRef, idRef);

    const invoice = ref<Invoice | null>(null);

    const { uploadFile } = useAiAssistant();
    const { validateFormat } = useValidFileFormat();
    const fileInput = ref<HTMLInputElement | null>(null);
    const imageInput = ref<HTMLInputElement | null>(null);

    const { t } = useTranslation();
    const { $price } = useNuxtApp();

    const documentTypeLabel = computed(() => {
        const docType = invoice.value?.documentType ?? 'invoice';
        const key = ENTITY_LABEL_KEY_MAP[docType];
        return key ? t(key) : t('ai.entity.document');
    });

    const authStore = useAuthStore();
    const renderText = (text: string | null | undefined): string => {
        if (!text || !invoice.value) return '';
        return replaceInvoiceVariables(text, invoice.value, authStore.tenant ?? null, $price);
    };

    const numberOrDraft = computed(() => invoice.value?.number ?? t('invoice_status.draft'));

    const sections = computed(() => {
        const items = invoice.value?.invoiceSections?.items ?? [];
        return items;
    });

    const handleFileSelect = (event: Event) => {
        const input = event.target as HTMLInputElement;
        validateFormat(input.files, (validFiles: File[]) => {
            validFiles.forEach((file) => uploadFile(file, props.id));
        });
        input.value = '';
    };

    const fetchData = async () => {
        const endpoint = EndpointFactory.make(ModelType.INVOICES) as InvoiceEndpoint;
        // setInclude takes a SINGLE comma-separated string. Need lines via the nested path so
        // section.invoiceLines.items is populated (otherwise it's null and the table is empty).
        const response = await endpoint
            .setInclude('contact,worksite,invoiceSections,invoiceSections.invoiceLines,invoiceSections.invoiceLines.vatRate')
            .retrieve(props.id);
        invoice.value = response.data as Invoice;
    };

    setRefreshCallback(fetchData);

    watch(
        () => props.id,
        () => fetchEntity(fetchData),
        { immediate: true },
    );

    const openHeavyPreview = () => {
        invoice.value?.preview();
    };

    const lineSubtotal = (line: { price: number | null; quantity: number | null }) => {
        return (line.price ?? 0) * (line.quantity ?? 0);
    };
</script>

<template>
    <div class="flex flex-col h-full">
        <input ref="fileInput" type="file" class="hidden" multiple @change="handleFileSelect" />
        <input ref="imageInput" type="file" accept="image/*" class="hidden" multiple @change="handleFileSelect" />

        <!-- Header -->
        <div class="flex items-center gap-[10px] px-[14px] py-[10px] border-b border-[#E7E7E7] min-h-[50px]">
            <button
                type="button"
                class="w-7 h-7 inline-flex items-center justify-center rounded-md text-[#42484E] hover:bg-[#F3F3F3] transition-colors cursor-pointer"
                @click="$emit('close')"
            >
                <i class="fa-solid fa-arrow-left text-[14px]" />
            </button>
            <span class="font-bricolage font-bold text-[14px] text-[#0F1020] truncate flex-1 leading-none">
                {{ invoice?.title ?? documentTypeLabel }}
            </span>
            <button
                type="button"
                class="w-7 h-7 inline-flex items-center justify-center rounded-md text-[#42484E] hover:bg-[#F3F3F3] transition-colors cursor-pointer"
                @click="fileInput?.click()"
            >
                <i class="fa-solid fa-paperclip text-[14px]" />
            </button>
            <button
                type="button"
                class="w-7 h-7 inline-flex items-center justify-center rounded-md text-[#42484E] hover:bg-[#F3F3F3] transition-colors cursor-pointer"
                @click="imageInput?.click()"
            >
                <i class="fa-regular fa-image text-[14px]" />
            </button>
            <AiPageEntityPreviewActions
                :type="invoice?.documentType ?? 'invoice'"
                :id="id"
                :invoice-status="invoice?.status"
                :invoice-document-type="invoice?.documentType"
                @preview="openHeavyPreview"
            />
        </div>

        <!-- Body -->
        <div v-if="loading" class="flex-1 flex items-center justify-center">
            <i class="fa-solid fa-spinner fa-spin text-gray-400" />
        </div>
        <div v-else-if="error" class="flex-1 flex items-center justify-center">
            <p class="text-[13px] text-gray-400">{{ $t('ai.preview.unavailable') }}</p>
        </div>
        <div v-else-if="invoice" class="flex-1 overflow-y-auto nice-scrollbar bg-[#F9FAFB]">
            <div class="m-5 p-7 bg-white rounded-[8px] border border-[#E7E7E7] shadow-[0_1px_10px_rgba(0,0,0,0.04)]">
                <!-- Title row -->
                <div class="flex justify-between items-start gap-4 mb-6">
                    <div class="min-w-0 flex-1">
                        <h1 class="font-bricolage font-bold text-[22px] text-[#0F1020] m-0 leading-tight">
                            {{ invoice.title ?? documentTypeLabel }}
                        </h1>
                        <div class="mt-1 font-inter text-[12px] text-[#717171]">
                            {{ documentTypeLabel }} · <span class="font-poppins text-[#0F1020]">{{ numberOrDraft }}</span>
                        </div>
                        <div v-if="invoice.trueDate" class="mt-1.5 font-inter text-[12px] text-[#717171]">
                            {{ $t('form.labels.documentDate') }}: <b class="text-[#0F1020]">{{ invoice.trueDate.format('DD/MM/YYYY') }}</b>
                        </div>
                        <div class="font-inter text-[12px] text-[#717171]">
                            {{ $t('form.labels.dueAt') }}:
                            <b class="text-[#0F1020]">
                                {{ invoice.dueAt ? invoice.dueAt.format('DD/MM/YYYY') : $t('form.labels.will_be_generated') }}
                            </b>
                        </div>
                        <div v-if="invoice.purchaseOrder" class="font-inter text-[12px] text-[#717171]">
                            {{ $t('form.labels.purchaseOrder') }}: <b class="text-[#0F1020]">{{ invoice.purchaseOrder }}</b>
                        </div>
                        <div v-if="invoice.buyerReference" class="font-inter text-[12px] text-[#717171]">
                            {{ $t('form.labels.buyerReference') }}: <b class="text-[#0F1020]">{{ invoice.buyerReference }}</b>
                        </div>
                    </div>
                    <InvoicingInvoiceStatusBadge
                        v-if="invoice.status && invoice.status !== 'draft'"
                        :invoice="invoice"
                    />
                </div>

                <!-- Contact / Worksite -->
                <div
                    v-if="invoice.contact || invoice.worksite"
                    class="grid grid-cols-2 gap-[30px] mb-6 font-inter text-[12px] text-[#42484E] leading-[1.55]"
                >
                    <div v-if="invoice.contact">
                        <div class="text-[10px] uppercase tracking-wide text-[#8D929C] mb-1">{{ $t('form.labels.client') }}</div>
                        <div class="font-semibold text-[#0F1020]">{{ invoice.contact.displayName }}</div>
                        <div v-if="invoice.contact.email">{{ invoice.contact.email }}</div>
                        <div v-if="invoice.contact.phone">{{ invoice.contact.phone }}</div>
                        <div v-if="invoice.contact.address?.display">{{ invoice.contact.address.display }}</div>
                    </div>
                    <div v-if="invoice.worksite">
                        <div class="text-[10px] uppercase tracking-wide text-[#8D929C] mb-1">{{ $t('form.labels.worksite') }}</div>
                        <div class="font-semibold text-[#0F1020]">{{ invoice.worksite.title }}</div>
                        <div v-if="invoice.worksite.address?.display">{{ invoice.worksite.address.display }}</div>
                    </div>
                </div>

                <!-- Description / preamble -->
                <div
                    v-if="invoice.description"
                    class="mb-6 px-3 py-3 rounded-[6px] bg-[#F9FAFB] border border-[#EDEFF5] font-inter text-[12px] text-[#42484E] leading-[1.6] whitespace-pre-wrap"
                >
                    {{ renderText(invoice.description) }}
                </div>

                <!-- Sections + lines -->
                <div
                    v-for="section in sections"
                    :key="section.getId()"
                    class="mb-5"
                >
                    <h3
                        v-if="section.title"
                        class="font-bricolage font-bold text-[16px] text-[#0F1020] mb-3 m-0"
                    >
                        {{ section.title }}
                    </h3>
                    <table class="w-full font-inter text-[12px] border-collapse">
                        <thead>
                            <tr class="border-b border-[#E7E7E7]">
                                <th class="text-left py-2 px-1 font-medium text-[#717171]">{{ $t('form.labels.description') }}</th>
                                <th class="text-right py-2 px-1 font-medium text-[#717171] w-[80px]">{{ $t('form.labels.price') }}</th>
                                <th class="text-right py-2 px-1 font-medium text-[#717171] w-[60px]">{{ $t('form.labels.quantity') }}</th>
                                <th class="text-right py-2 px-1 font-medium text-[#717171] w-[60px]">{{ $t('form.labels.vat') }}</th>
                                <th class="text-right py-2 px-1 font-medium text-[#717171] w-[100px]">{{ $t('form.labels.subtotal') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="line in section.invoiceLines?.items ?? []"
                                :key="line.getId()"
                                class="border-b border-[#EDEFF5] align-top"
                            >
                                <td class="py-2 px-1 text-[#0F1020]">
                                    <div>{{ renderText(line.title) }}</div>
                                    <div
                                        v-if="line.description"
                                        class="mt-0.5 text-[11px] text-[#717171] whitespace-pre-wrap"
                                    >
                                        {{ renderText(line.description) }}
                                    </div>
                                    <div
                                        v-if="line.discount"
                                        class="mt-0.5 text-[10px] uppercase tracking-wide text-[#E8B102]"
                                    >
                                        -{{ line.discount }}%
                                    </div>
                                </td>
                                <td class="py-2 px-1 text-right text-[#42484E] font-poppins">{{ $price(line.price ?? 0) }}</td>
                                <td class="py-2 px-1 text-right text-[#42484E] font-poppins">{{ line.quantity ?? 0 }}</td>
                                <td class="py-2 px-1 text-right text-[#42484E] font-poppins">
                                    {{ line.vatRate?.rate != null ? line.vatRate.rate + '%' : '—' }}
                                </td>
                                <td class="py-2 px-1 text-right text-[#0F1020] font-poppins font-medium">{{ $price(lineSubtotal(line)) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Totals -->
                <div class="mt-5 flex justify-end">
                    <table class="font-inter text-[13px]">
                        <tbody>
                            <tr>
                                <td class="py-1 pr-4 text-[#717171]">{{ $t('form.labels.subtotal') }}</td>
                                <td class="text-right font-poppins font-medium text-[#0F1020] min-w-[100px]">{{ $price(invoice.subtotal ?? 0) }}</td>
                            </tr>
                            <tr v-if="invoice.totalTax">
                                <td class="py-1 pr-4 text-[#717171]">{{ $t('form.labels.vat') }}</td>
                                <td class="text-right font-poppins text-[#42484E]">{{ $price(invoice.totalTax) }}</td>
                            </tr>
                            <tr class="border-t border-[#E7E7E7]">
                                <td class="pt-2 pr-4 text-[#0F1020] font-semibold">{{ $t('form.labels.total') }}</td>
                                <td class="text-right font-poppins font-bold text-[15px] text-[#0F1020]">{{ $price(invoice.total ?? 0) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Conditions / payment terms -->
                <div
                    v-if="invoice.conditions"
                    class="mt-7 pt-5 border-t border-[#E7E7E7]"
                >
                    <div class="text-[10px] uppercase tracking-wide text-[#8D929C] mb-2">{{ $t('form.labels.conditions') }}</div>
                    <div class="font-inter text-[12px] text-[#42484E] leading-[1.6] whitespace-pre-wrap">
                        {{ renderText(invoice.conditions) }}
                    </div>
                </div>

                <!-- Belgian structured communication -->
                <div
                    v-if="invoice.belgianStructuredCommunication"
                    class="mt-5 px-3 py-2 rounded-[6px] bg-[#F9FAFB] border border-[#EDEFF5] font-inter text-[12px] text-[#42484E]"
                >
                    <span class="text-[10px] uppercase tracking-wide text-[#8D929C] mr-2">{{ $t('form.labels.structured_communication') }}:</span>
                    <span class="font-poppins font-medium text-[#0F1020]">{{ invoice.belgianStructuredCommunication }}</span>
                </div>
            </div>
        </div>
    </div>
</template>
